import { kObject, kObjectArray } from "../ReUsableTS/kBrowserObjects";
import type { kTime } from "../ReUsableTS/kBrowserObjects";
import ks from "../ReUsableTS/kBrowserShared";
import { kStyles } from "./kStyles";
import { AsKTimeOrNull, kBuildSelectEditOptions, kEdTblSelectionMode, kEditableTableActive, kHTMLEditableColType, kNaturalStringCompare } from "./kEditableTableTypes";
import type { kRelativeTimeFormat, kRelativeTimeFormatCtor, kRichHtmlCellEditorHost } from "./kEditableTableTypes";
import { kEditableColumn, kNewChoiceCollector } from "./kEditableColumn";
import LNG from "../ReUsableTS/kLNG";
import { kDateInput, kSelect } from "../ReUsableTS/kHTML";
import { kHTMLMultiSelect } from "../ReUsableTS/kHTMLMultiSelect";
import type { kEditableTable } from "./kEditableTable";
import { kEditableTableCellPaint } from "./kEditableTableCellPaint";
import { kEditableTableCellInput } from "./kEditableTableCellInput";
import { kEditableTableCellValue } from "./kEditableTableCellValue";
import { kEditableTableCellEdit } from "./kEditableTableCellEdit";
import type { kEditableTableRow } from "./kEditableTableRow";

// kEditableTableCell / kEditableTableCells, moved out of kEditableTable.ts (size split, 2026-10-20); re-exported from kEditableTable.ts.

export class kEditableTableCell extends kObject{
    private static readonly DIRTY_DEBOUNCE_MS: number = 300;
    // Sentinel <option> value for the always-on "add new item" entry a Select
    // dropdown appends. Distinct from any real choice text (which becomes the
    // option's textContent) so it is collision-proof; the edit-complete path
    // must NEVER commit it as a value.
    public static readonly ADD_NEW_CHOICE: string = "__kEdTbl_AddNewChoice__";

    // The radio and checkbox glyphs' box styling, as kStyles class rules (2026-10-14). It used to be a
    // style="..." attribute in the markup, which a Content-Security-Policy without style-src
    // 'unsafe-inline' refuses; class rules work on both kStyles paths (<style> or adopted sheets).
    // Same declarations as the old attributes, so the glyphs look exactly as before.
    private static _GlyphStylesSaved: boolean = false;
    private static _EnsureGlyphStyles = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kEditableTableCell._GlyphStylesSaved === true) { return; }
            kStyles.Add("kEdRadioOn", "display:inline-block;width:18px;height:18px;border-radius:50%;border:2px solid #0078d4;background:#0078d4;box-shadow:inset 0 0 0 4px #ffffff;box-sizing:border-box;vertical-align:middle", false);
            kStyles.Add("kEdRadioOff", "display:inline-block;width:18px;height:18px;border-radius:50%;border:1.5px solid #cfd4db;background:#eef1f5;box-sizing:border-box;vertical-align:middle", false);
            kStyles.Add("kEdChkBoxOn", "display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:5px;border:1.5px solid #222B33;background:#C3CFDB;box-sizing:border-box;vertical-align:middle", false);
            kStyles.Add("kEdChkBoxOff", "display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:5px;border:1.5px solid #cfd4db;background:#eef1f5;box-sizing:border-box;vertical-align:middle", false);
            kStyles.Save();
            kEditableTableCell._GlyphStylesSaved = true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCell._EnsureGlyphStyles");
        }
    };
    public _RadioHtml = (On: boolean): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            kEditableTableCell._EnsureGlyphStyles();
            if (On) {
                return `<span class="kEdRadioOn"></span>`;
            }
            return `<span class="kEdRadioOff"></span>`;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCell._RadioHtml");
            return "";
        }
    }
    /** ` aria-label="..."` for this cell's checkbox - its column's CheckboxLabel - or "" when the column
     *  names none (the long-standing markup). A role=checkbox with no name is announced as just
     *  "checkbox" by a screen reader; found on every row of a kPickGrid (SPArchive audit, 2026-10-01). */
    private _CheckboxAria = (): string => kEditableTableCellPaint._CheckboxAria(this);
    public _CheckboxHtml = (On: boolean): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            kEditableTableCell._EnsureGlyphStyles();
            const Aria: string = this._CheckboxAria();
            if (On) {
                // The tick is ALWAYS rendered (both checked and unchecked); CSS shows
                // it when the box carries .kEdChkOn (stroke-dashoffset 0) and hides it
                // otherwise (dashoffset 24), so a resting unchecked box shows no tick.
                // Keeping it in the DOM both ways is what lets a real user toggle
                // reverse-WIPE it on uncheck (see _PlayCheckboxAnim + AddStyles).
                // Discreet grey-blue palette: dark blue #222B33 tick + border, light
                // blue #C3CFDB fill when checked.
                return `<span class="kEdChk kEdChkOn kEdChkBoxOn" role="checkbox" aria-checked="true"${Aria}><svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"><path class="kEdChkPath" d="M5 12.5 L10 17.5 L19 6.5" fill="none" stroke="#222B33" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"></path></svg></span>`;
            }
            return `<span class="kEdChk kEdChkBoxOff" role="checkbox" aria-checked="false"${Aria}><svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"><path class="kEdChkPath" d="M5 12.5 L10 17.5 L19 6.5" fill="none" stroke="#222B33" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"></path></svg></span>`;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCell._CheckboxHtml");
            return "";
        }
    }
    private _PlayCheckboxAnim = (): void => kEditableTableCellPaint._PlayCheckboxAnim(this);

    // ---- Cell-decoration renderers (same jqWidgets-free, listener-free family as
    // _RadioHtml / _CheckboxHtml): compact, non-interactive visuals painted into a
    // read-mode cell. The cell stays the sole click/edit target; these add no
    // listeners of their own. Kept here rather than reusing kPill because a badge
    // inside an already-clickable editable cell must NOT carry kPill's button
    // click/hover/cursor semantics or a per-instance listener at grid scale.

    // Curated soft chip palette (light-bg + readable-text pairs — chart colours are
    // saturated singles that do not suit a text-bearing chip). Static literal field:
    // built once, and exempt from the method-length / error-pattern rules.
    private static _ChipPalette: { Bg: string; Border: string; Text: string }[] = [
        { Bg: "#eef2ff", Border: "#c7d2fe", Text: "#3730a3" },
        { Bg: "#ecfdf5", Border: "#a7f3d0", Text: "#065f46" },
        { Bg: "#fef2f2", Border: "#fecaca", Text: "#991b1b" },
        { Bg: "#fffbeb", Border: "#fde68a", Text: "#92400e" },
        { Bg: "#eff6ff", Border: "#bfdbfe", Text: "#1e40af" },
        { Bg: "#fdf4ff", Border: "#f5d0fe", Text: "#86198f" },
        { Bg: "#f0fdfa", Border: "#99f6e4", Text: "#115e59" },
        { Bg: "#fff7ed", Border: "#fed7aa", Text: "#9a3412" }
    ];

    // Stable value -> palette index so a given choice always draws the same colour.
    private _ChipPaletteIndex = (Value: string): number => {
        if (ks.KErrorMode === true) { return 0; }
        try {
            let Hash: number = 0;
            let CurrChar: string;
            for (CurrChar of Value) {
                Hash = ((Hash * 31) + CurrChar.charCodeAt(0)) | 0;
            }
            return Math.abs(Hash) % kEditableTableCell._ChipPalette.length;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCell._ChipPaletteIndex");
            return 0;
        }
    };

    // Build one chip span. Text is set via textContent (never innerHTML) so a data
    // value containing HTML-special characters can never inject markup.
    private _MakeChipEl = (Label: string): HTMLSpanElement => {
        if (ks.KErrorMode === true) { return document.createElement("span"); }
        try {
            const Span: HTMLSpanElement = document.createElement("span");
            Span.className = "kEdChip";
            const Palette: { Bg: string; Border: string; Text: string } = kEditableTableCell._ChipPalette[this._ChipPaletteIndex(Label)];
            Span.textContent = Label;
            // FIXED 20px height (not a tall line-height): the read-mode cell content
            // box is ~20px, so a chip whose natural height exceeds that grows the row
            // when the pill appears (e.g. picking a value into a blank cell jumped the
            // row 23->29px - runtime-measured 2026-07-06). A deterministic 20px box
            // (border-box, so the 1px border is included) fits the cell exactly and is
            // host-agnostic - SharePoint's line-height cannot inflate it. Vertical
            // centring is handled by align-items:center, so line-height is irrelevant.
            Span.style.cssText = "display:inline-flex;align-items:center;max-width:100%;height:20px;padding:0 10px;border-radius:999px;font-size:12px;font-weight:600;line-height:1;box-sizing:border-box;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;vertical-align:middle";
            Span.style.background = Palette.Bg;
            Span.style.border = "1px solid " + Palette.Border;
            Span.style.color = Palette.Text;
            return Span;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCell._MakeChipEl");
            return document.createElement("span");
        }
    };
    public _PaintSelectChip = (Value: string): void => kEditableTableCellPaint._PaintSelectChip(this, Value);
    public _PaintMultiChips = (Value: string): void => kEditableTableCellPaint._PaintMultiChips(this, Value);
    public _PaintColorSwatch = (Value: string | number | boolean | null | undefined): void => kEditableTableCellPaint._PaintColorSwatch(this, Value);
    public _PaintDataBar = (ColumnMax: number): void => kEditableTableCellPaint._PaintDataBar(this, ColumnMax);
    public _ApplyDateRelativeTooltip = (Value: any): void => kEditableTableCellPaint._ApplyDateRelativeTooltip(this, Value);
    constructor() {
        super();
        this.Cell = document.createElement("td");
        this.Cell.className = "kEditableTableCell";
        this.Cell.setAttribute("role", "gridcell");
        this.Cell.addEventListener("click", this.CellClick);
        this.Cell.addEventListener("contextmenu",this._CellRightClick);
        this.Cell.addEventListener("mouseover",this._MouseOver);
        this.Cell.addEventListener("mousedown", this._MouseDown);
    }
    public SelectionMode:kEdTblSelectionMode = kEdTblSelectionMode.NoSelection;
    public OrgValue:any = null;
    public CurrentValue:any = null;
    public Dirty:boolean = false;

    public Cell: HTMLTableCellElement;
    private _ValidationIcon: HTMLSpanElement | null = null;
    public ColDef:kEditableColumn= new kEditableColumn();
    public ParentTable:kEditableTable|null = null;

    public RowIndex:number = 0;
    public ColumnIndex:number = 0;

    public ChoiceDD:kSelect|null = null;
    public ChoiceMultiDD:kHTMLMultiSelect|null = null;
    // Set while the "add new item" dialog / consumer callback is open so the
    // edit-complete path skips committing the sentinel option as a value.
    private _AddingNewChoice: boolean = false;
    public DatePicker:kDateInput |null = null;
    public ColorPicker:HTMLInputElement|null = null;
    public HtmlPopup:kRichHtmlCellEditorHost|null = null;
    private _MouseOver = (Event:MouseEvent): void => kEditableTableCellInput._MouseOver(this, Event);
    private _MouseDownKeepsOpenEditor = (EditCell: kEditableTableCell, ShiftHeld: boolean): boolean => kEditableTableCellInput._MouseDownKeepsOpenEditor(this, EditCell, ShiftHeld);
    private _MouseDown = (_Event: MouseEvent): void => kEditableTableCellInput._MouseDown(this, _Event);

    public CellClick = (Event?:MouseEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            let CurrTarget: HTMLElement|undefined = undefined;
            if(Event){
                CurrTarget = <HTMLElement> Event.target;
            }
            if (this._ConsumeInPlaceEditClick(Event) === true) { return; }
            // A click on a REAL hyperlink inside a read-mode cell is NAVIGATION,
            // not a cell gesture — see _IsRealLinkTarget for why.
            if (kEditableTableCell._IsRealLinkTarget(CurrTarget) === true) {
                if (Event) { Event.stopPropagation(); }
                return;
            }
            const IsFormElement: boolean = CurrTarget instanceof HTMLSelectElement
                || CurrTarget instanceof HTMLInputElement
                || CurrTarget instanceof HTMLTextAreaElement;
            if(Event === undefined || undefined === CurrTarget || CurrTarget === this.Cell || (this.Cell.contains(CurrTarget) && !IsFormElement)){
                if (this.ParentTable) {
                    if (undefined === Event || Event.shiftKey === false) {
                        if (this._HandlePrimaryClick(Event) === false) { return; }
                    } else {
                        this.ParentTable.Selection.SelectionChangeMulti(this);
                    }
                }
                if(Event){
                    Event.stopPropagation();
                    Event.preventDefault();
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCell.CellClick");
            return;
        }
    }
    private _ConsumeInPlaceEditClick = (Event?:MouseEvent): boolean => kEditableTableCellInput._ConsumeInPlaceEditClick(this, Event);
    private _HandlePrimaryClick = (Event?:MouseEvent): boolean => kEditableTableCellInput._HandlePrimaryClick(this, Event);
    /** Opens the editor the column's Type calls for (no CustomEditor declared). */
    private _BeginEditForType = (Event?:MouseEvent): void => kEditableTableCellInput._BeginEditForType(this, Event);

    // ONE TAB STOP PER GRID (2026-10-14): the <table> is the grid's tab stop (_PaintGridRole). A
    // control a CustomRenderer paints into a cell leaves the TAB ORDER (tabindex -1) ONLY when Enter
    // can reach it instead - it is the cell's SOLE focusable control and a real link or an enabled
    // button (SoleActivatableControl; kEditableTable._ActivateCellControl clicks the same one). A cell
    // with several controls, an input, a "#" link or a disabled button keeps them all tabbable (review
    // of change 104: otherwise such a cell lost keyboard access). Measured before, in Storage Clean-up
    // on SPSE: one tab stop per row (~1,000 Tab presses past 1,000 rows).
    public static KeepOutOfTabOrder = (Root: HTMLElement | null): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Control: HTMLElement | null = kEditableTableCell.SoleActivatableControl(Root);
            if (Control !== null) { Control.setAttribute("tabindex", "-1"); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCell.KeepOutOfTabOrder");
        }
    };

    // The ONE rule both sides share: Root's only focusable control, when it is a real link (href not
    // empty, "#" or javascript:) or an enabled button; null in every other case (none, several, an
    // input / select / textarea / [tabindex] element, a "#" link, a disabled button).
    public static SoleActivatableControl = (Root: HTMLElement | null): HTMLElement | null => {
        if (ks.KErrorMode === true) { return null; }
        try {
            if (Root === null) { return null; }
            const Focusable: HTMLElement[] = Array.prototype.slice.call(
                Root.querySelectorAll("a[href], button, input, select, textarea, [tabindex]")) as HTMLElement[];
            if (Focusable.length !== 1) { return null; }
            const Control: HTMLElement = Focusable[0];
            if (Control.tagName === "BUTTON") {
                if ((Control as HTMLButtonElement).disabled === true) { return null; }
                return Control;
            }
            if (Control.tagName !== "A") { return null; }
            const Href: string = ks.Nz(Control.getAttribute("href"));
            if (Href === "" || Href === "#" || Href.toLowerCase().indexOf("javascript:") === 0) { return null; }
            return Control;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCell.SoleActivatableControl");
            return null;
        }
    };

    // True when the click landed on (or inside) an <a> carrying a real navigable href
    // (http/https/mailto/tel/absolute path — NOT a bare "#" or a javascript: scheme).
    // Lets a hyperlink rendered into a read-mode cell via a CustomRenderer navigate,
    // instead of being swallowed by CellClick's default-cancel. Pure/static; guarded
    // and reported like the rest of the class, safe-defaulting to false (cancel as before).
    private static _IsRealLinkTarget = (Target: HTMLElement | undefined): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Target === undefined) { return false; }
            const Anchor: Element | null = Target.closest("a");
            if (Anchor === null) { return false; }
            const Href: string = ks.Nz(Anchor.getAttribute("href"));
            if (Href === "" || Href === "#") { return false; }
            if (Href.toLowerCase().indexOf("javascript:") === 0) { return false; }
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCell._IsRealLinkTarget");
            return false;
        }
    }
    private _MountInlineEditor = (Editor: HTMLElement): void => kEditableTableCellInput._MountInlineEditor(this, Editor);
    private _EditColor = (): void => kEditableTableCellInput._EditColor(this);
    // The options of the <select> editor opened by _EditSelect.
    private _FillSelectEditor = (Editor: kSelect, ColDef: kEditableColumn): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            // Build a LOCAL option list (the column's shared Options are never
            // mutated): configured choices + a trailing blank-to-clear + the
            // cell's CURRENT value injected when it is not a configured choice.
            // Without that injection the <select> cannot represent a stored
            // value the column did not list (e.g. a code from a wider locale
            // dataset), falls back to its first option, and a no-change blur
            // then COMMITS that first option - silently destroying the value
            // (law 1: never lose data). Root-caused 2026-06-28.
            const EditOptions: string[] = kBuildSelectEditOptions(ColDef.Options, ks.Nz(this.Cell.textContent));
            Editor.AddChoices(EditOptions);
            this._TitleSelectOptions(Editor.Select);
            // Always-on "add new item" entry at the foot of the dropdown (unless
            // the column opts out). It carries the collision-proof sentinel VALUE
            // while showing a friendly label — picking it opens the add-new flow
            // instead of committing (see ChoiceDDChanged / _CompleteSelectEdit).
            if (ColDef.AllowAddNewItem === true) {
                const AddOption: HTMLOptionElement = Editor.AddChoice("+ " + LNG.ADDNEW);
                AddOption.value = kEditableTableCell.ADD_NEW_CHOICE;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCell._FillSelectEditor");
        }
    }
    private _EditSelect = (): void => kEditableTableCellInput._EditSelect(this);
    private _EditSelectMulti = (): void => kEditableTableCellInput._EditSelectMulti(this);
    /**
     * Shared inline-edit entry: run the caller's pre-step (Before), make the cell
     * editable when Editable, seed/replace its text through the caller's Seed lambda
     * when given, focus it, select its contents (collapsed to a caret at the end when
     * Collapse), and arm the beforeinput guard when ArmGuard. Every axis a caller left
     * out in the originals is a flag here, so each delegation reads as the original
     * body. Seed/Before lambdas run inside this guard exactly where the originals ran
     * those steps. The KGlobalErrorHandler label is always the caller's own.
     */
    private _EnterInlineEdit = (Editable: boolean, Seed: (() => string) | null, Before: (() => void) | null, Collapse: boolean, ArmGuard: boolean, Label: string): void => kEditableTableCellInput._EnterInlineEdit(this, Editable, Seed, Before, Collapse, ArmGuard, Label);
    private _EditTextOrNumber = (): void => kEditableTableCellInput._EditTextOrNumber(this);
    public BeginTextEditWithChar = (Ch: string): void => kEditableTableCellInput.BeginTextEditWithChar(this, Ch);
    public PlaceCaretAtEnd = (): void => kEditableTableCellInput.PlaceCaretAtEnd(this);
    public SyncInlineValueForHarvest = (): void => kEditableTableCellInput.SyncInlineValueForHarvest(this);
    private _EditCurrency = (): void => kEditableTableCellInput._EditCurrency(this);
    private _EditRichHtml = (): void => kEditableTableCellInput._EditRichHtml(this);
    private _EditDate = (): void => kEditableTableCellInput._EditDate(this);
    private _CellRightClick = (Event:MouseEvent): void => kEditableTableCellInput._CellRightClick(this, Event);
    private _DirtyDebounceTimer: number = 0;
    private _FireDirtiedCallback = (): void => kEditableTableCellValue._FireDirtiedCallback(this);
    public SetDirty(): void {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Dirty = true;
            this.Cell.style.fontWeight = "bold";
            if (this.ParentTable) {
                this.ParentTable.RecordEdit(this, this.OrgValue, this.CurrentValue);
            }
            window.clearTimeout(this._DirtyDebounceTimer);
            this._DirtyDebounceTimer = window.setTimeout(this._FireDirtiedCallback, kEditableTableCell.DIRTY_DEBOUNCE_MS);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCell.SetDirty");
            return;
        }
    }
    public SetClean = (): void => kEditableTableCellValue.SetClean(this);
    public SetNewValue = (NewValue:any, SetDirty?:boolean): void => kEditableTableCellValue.SetNewValue(this, NewValue, SetDirty);

    // When the column declares a CustomRenderer it fully owns the
    // visualisation: skip the default switch (which would overwrite
    // with String(value), so an object value renders as "[object
    // Object]" and an HTML string renders as raw markup) and let
    // the renderer paint the cell after we have updated the
    // stored value + dirty state. Returns true when it took over —
    // SetNewValue then does nothing more.
    private _PaintCustomRendered = (NewValue:any, SetDirty?:boolean): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (this.Cell === null || this.ColDef === undefined
                || this.ColDef.CustomRenderer === null) { return false; }
            if (NewValue !== this.OrgValue) {
                this.CurrentValue = NewValue;
                if (SetDirty !== false) { this.SetDirty(); }
            } else {
                this.SetClean();
            }
            this.ColDef.CustomRenderer(NewValue, this);
            kEditableTableCell.KeepOutOfTabOrder(this.Cell);
            this._ApplyValidation();
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCell._PaintCustomRendered");
            return false;
        }
    };
    /** The per-column-type paint switch of SetNewValue. */
    private _PaintNewValue = (NewValue:any, SetDirty?:boolean): void => kEditableTableCellValue._PaintNewValue(this, NewValue, SetDirty);
    /** `Date` branch of SetNewValue's type switch. */
    private _SetValueDate = (NewValue:any, SetDirty?:boolean): void => kEditableTableCellValue._SetValueDate(this, NewValue, SetDirty);
    /** `Color` branch of SetNewValue's type switch. */
    private _SetValueColor = (NewValue:any, SetDirty?:boolean): void => kEditableTableCellValue._SetValueColor(this, NewValue, SetDirty);
    /** `Number` branch of SetNewValue's type switch. */
    private _SetValueNumber = (NewValue:any, SetDirty?:boolean): void => kEditableTableCellValue._SetValueNumber(this, NewValue, SetDirty);
    /** `Currency` branch of SetNewValue's type switch. */
    private _SetValueCurrency = (NewValue:any, SetDirty?:boolean): void => kEditableTableCellValue._SetValueCurrency(this, NewValue, SetDirty);
    /** `SelectMulti` branch of SetNewValue's type switch. */
    private _SetValueSelectMulti = (NewValue:any, SetDirty?:boolean): void => kEditableTableCellValue._SetValueSelectMulti(this, NewValue, SetDirty);
    /** `Checkbox` branch of SetNewValue's type switch. */
    private _SetValueCheckbox = (NewValue:any, SetDirty?:boolean): void => kEditableTableCellValue._SetValueCheckbox(this, NewValue, SetDirty);

    /**
     * CellDirtiedCB for a checkbox the user toggled back to where it started. SetDirty is what arms that
     * callback, and a value equal to the original is "clean" - so the SECOND click on a box told the host
     * nothing: a pick grid's "Selected: 1" and its action buttons stayed as if the row were still ticked
     * (SPArchive live E2E, 2026-10-01), and a host that persists on the callback kept the first click.
     * Same debounce as SetDirty; the cell is NOT marked dirty and nothing goes on the undo stack.
     */
    private _NotifyToggledBack = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            window.clearTimeout(this._DirtyDebounceTimer);
            this._DirtyDebounceTimer = window.setTimeout(this._FireDirtiedCallback, kEditableTableCell.DIRTY_DEBOUNCE_MS);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCell._NotifyToggledBack");
        }
    };
    /** `Radio` branch of SetNewValue's type switch. */
    private _SetValueRadio = (NewValue:any, SetDirty?:boolean): void => kEditableTableCellValue._SetValueRadio(this, NewValue, SetDirty);
    /** Turns every OTHER selected radio in this column off. */
    private _DeselectOtherRadios = (): void => kEditableTableCellValue._DeselectOtherRadios(this);
    /** `RichHtml` branch of SetNewValue's type switch. */
    private _SetValueRichHtml = (NewValue:any, SetDirty?:boolean): void => kEditableTableCellValue._SetValueRichHtml(this, NewValue, SetDirty);
    /** `Text` branch of SetNewValue's type switch. */
    private _SetValueText = (NewValue:any, SetDirty?:boolean): void => kEditableTableCellValue._SetValueText(this, NewValue, SetDirty);
    /** `Select` branch of SetNewValue's type switch. */
    private _SetValueSelect = (NewValue:any, SetDirty?:boolean): void => kEditableTableCellValue._SetValueSelect(this, NewValue, SetDirty);
    /** `default` branch of SetNewValue's type switch. */
    private _SetValueDefault = (NewValue:any, SetDirty?:boolean): void => kEditableTableCellValue._SetValueDefault(this, NewValue, SetDirty);
    private _ApplyOptionTooltip = (Value: string): void => kEditableTableCellValue._ApplyOptionTooltip(this, Value);
    private _TitleSelectOptions = (Select: HTMLSelectElement): void => kEditableTableCellValue._TitleSelectOptions(this, Select);
    private _PaintRichHtmlPreview = (Html: string): void => kEditableTableCellValue._PaintRichHtmlPreview(this, Html);
    private _OwningRowIsEmpty = (): boolean => kEditableTableCellValue._OwningRowIsEmpty(this);
    private _ApplyValidation = (): void => kEditableTableCellValue._ApplyValidation(this);
    private _TextEnterPrevent = (Event:any): void => kEditableTableCellValue._TextEnterPrevent(this, Event);
    public ColorChange = (): void => kEditableTableCellEdit.ColorChange(this);

    public ChoiceDDChanged = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if(this.ChoiceDD && this.ChoiceDD.Select){
                if (this.ChoiceDD.Select.value === kEditableTableCell.ADD_NEW_CHOICE) {
                    this._StartAddNewChoice();
                    return;
                }
                this.SetNewValue(ks.Nz(this.ChoiceDD.Select.value));
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCell.ChoiceDDChanged");
            return;
        }
    }
    private _StartAddNewChoice = (): void => kEditableTableCellEdit._StartAddNewChoice(this);
    private _OnNewSelectChoice = (NewItem: string): void => kEditableTableCellEdit._OnNewSelectChoice(this, NewItem);
    public DateInputChanged = (): void => kEditableTableCellEdit.DateInputChanged(this);
    public SetCellReadMode = (): void => kEditableTableCellEdit.SetCellReadMode(this);
    private _CompleteSelectEdit = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            // The add-new flow already tore the <select> down and is capturing
            // the name; committing here would write the sentinel over the value.
            if (this._AddingNewChoice === true) { return; }
            if (this.ChoiceDD && this.ChoiceDD.Select) {
                if (this.ChoiceDD.Select.value === kEditableTableCell.ADD_NEW_CHOICE) {
                    // Blurred with the sentinel highlighted but not chosen — drop it,
                    // keep the current value rather than committing the sentinel.
                    this.ChoiceDD.Select.remove();
                    this.ChoiceDD = null;
                    this.Cell.textContent = ks.Nz(this.CurrentValue);
                    return;
                }
                this.SetNewValue(ks.Nz(this.ChoiceDD.Select.value));
                this.ChoiceDD.Select.remove();
                this.ChoiceDD = null;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCell._CompleteSelectEdit");
        }
    }
    private _CompleteColorEdit = (): void => kEditableTableCellEdit._CompleteColorEdit(this);
    private _CompleteMultiSelectEdit = (): void => kEditableTableCellEdit._CompleteMultiSelectEdit(this);
    private _RequestNewMultiChoice = (): void => kEditableTableCellEdit._RequestNewMultiChoice(this);
    private _OnNewMultiChoice = (NewItem: string): void => kEditableTableCellEdit._OnNewMultiChoice(this, NewItem);
    private _CompleteRichHtmlEdit = (): void => kEditableTableCellEdit._CompleteRichHtmlEdit(this);
    private _CompleteDateEdit = (): void => kEditableTableCellEdit._CompleteDateEdit(this);
    /**
     * Shared inline-edit completion: leave edit mode, right-align a numeric cell, parse
     * the edited text through the caller's Parse lambda, commit when the parsed value
     * differs from OrgValue (the numeric callers mark dirty themselves; the text caller
     * passes SetNewValue's second argument exactly as before), grow a trailing row after
     * a committing last-row edit, restore the clean state when unchanged, and drop the
     * beforeinput guard. The KGlobalErrorHandler label is always the caller's own.
     */
    private _CompleteInlineEdit = (Parse: () => any, Numeric: boolean, Label: string): void => kEditableTableCellEdit._CompleteInlineEdit(this, Parse, Numeric, Label);
    private _CompleteTextEdit = (): void => kEditableTableCellEdit._CompleteTextEdit(this);
    public CancelTextEdit = (): void => kEditableTableCellEdit.CancelTextEdit(this);
    private _CompleteCurrencyEdit = (): void => kEditableTableCellEdit._CompleteCurrencyEdit(this);
    private _CompleteNumberEdit = (): void => kEditableTableCellEdit._CompleteNumberEdit(this);
    public SetCellOrgValue = (Value:any): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if(this.Cell && this.ColDef){
                this._PaintOrgValue(Value);
                this.OrgValue = Value;
                this.CurrentValue = Value;
                if (this.ColDef.CustomRenderer !== null) {
                    this.ColDef.CustomRenderer(Value, this);
                    kEditableTableCell.KeepOutOfTabOrder(this.Cell);
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCell.SetCellOrgValue");
            return;
        }
    }
    /** The read-mode paint half of SetCellOrgValue, per column type. */
    private _PaintOrgValue = (Value:any): void => kEditableTableCellEdit._PaintOrgValue(this, Value);
    /** Read-mode paint of a Date org value. */
    private _PaintOrgDate = (Value:any): void => kEditableTableCellEdit._PaintOrgDate(this, Value);
    /** Read-mode paint of a Number or Currency org value. */
    private _PaintOrgNumeric = (Value:any): void => kEditableTableCellEdit._PaintOrgNumeric(this, Value);
    public SelectNextDown = (): void => kEditableTableCellEdit.SelectNextDown(this);
    public SelectNextUp = (): void => kEditableTableCellEdit.SelectNextUp(this);
    public SelectNextRight = (): void => kEditableTableCellEdit.SelectNextRight(this);
    private SelectNextTo = (RowIndex: number, ColumnIndex: number, Label: string): void => kEditableTableCellEdit.SelectNextTo(this, RowIndex, ColumnIndex, Label);
    public SelectNextLeft = (): void => kEditableTableCellEdit.SelectNextLeft(this);
    public SelectNextRowFirstCell = (): void => kEditableTableCellEdit.SelectNextRowFirstCell(this);
    public SelectPrevRowLastCell = (): void => kEditableTableCellEdit.SelectPrevRowLastCell(this);
    public SetSelectionMode = (NewMode:kEdTblSelectionMode): void => kEditableTableCellEdit.SetSelectionMode(this, NewMode);
    public Select = (): void => kEditableTableCellEdit.Select(this);
}

export class kEditableTableCells extends kObjectArray{
    public Items:kEditableTableCell[] =[];
}
